import React, { useEffect, useRef, useState } from 'react';
import { siteData } from '../data/mock';
import { Linkedin } from 'lucide-react';

export const TeamSection = () => {
  const { team } = siteData;
  const [visible, setVisible] = useState(false);
  const [expandedMember, setExpandedMember] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="team"
      className="oa-section oa-grid-bg"
      style={{ borderTop: '1px solid var(--border-subtle)' }}
    >
      <div className="relative z-10" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Section Label */}
        <div
          className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ marginBottom: '24px' }}
        >
          <span className="oa-number">{team.number} // {team.label}</span>
        </div>

        {/* Headline */}
        <h2
          className={`oa-display-large transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ marginBottom: '24px', maxWidth: '600px' }}
        >
          {team.headline}
        </h2>

        {/* Body */}
        <p
          className={`oa-body-medium transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ marginBottom: '80px', maxWidth: '720px' }}
        >
          {team.body}
        </p>

        {/* Team Members */}
        <div className="flex flex-col gap-0" style={{ marginBottom: '80px' }}>
          {team.members.map((member, i) => {
            const isExpanded = expandedMember === i;
            return (
              <div
                key={i}
                className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{
                  transitionDelay: `${300 + i * 80}ms`,
                  borderTop: '1px solid var(--border-subtle)',
                  borderBottom: i === team.members.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                }}
              >
                {/* Header row */}
                <div
                  className="flex items-center justify-between gap-4"
                  style={{ padding: '28px 0', cursor: 'pointer' }}
                  onClick={() => setExpandedMember(isExpanded ? null : i)}
                >
                  <div className="flex items-center gap-5" style={{ flex: '1 1 0', minWidth: 0 }}>
                    {/* Avatar */}
                    <div
                      style={{
                        width: '56px',
                        height: '56px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: isExpanded ? 'rgba(0, 255, 209, 0.08)' : 'rgba(255,255,255,0.04)',
                        fontSize: '18px',
                        fontWeight: 600,
                        color: isExpanded ? 'var(--brand-primary)' : 'var(--text-muted)',
                        fontFamily: "'Inter', sans-serif",
                        letterSpacing: '2px',
                        flexShrink: 0,
                        transition: 'background 0.3s ease, color 0.3s ease',
                      }}
                    >
                      {member.initials}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <h3
                        className="oa-heading-2"
                        style={{
                          color: isExpanded ? 'var(--text-primary)' : 'var(--text-secondary)',
                          marginBottom: '2px',
                          transition: 'color 0.3s ease',
                        }}
                      >
                        {member.name}
                      </h3>
                      <span
                        style={{
                          fontSize: '14px',
                          color: 'var(--brand-primary)',
                          fontFamily: "'Inter', sans-serif",
                          opacity: 0.8,
                        }}
                      >
                        {member.role}
                      </span>
                    </div>
                  </div>

                  {/* Lead area - left aligned, single line */}
                  <span
                    className="hidden lg:block"
                    style={{
                      fontSize: '13px',
                      color: 'var(--text-muted)',
                      fontFamily: "'Inter', sans-serif",
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {member.lead}
                  </span>

                  {/* LinkedIn + Toggle */}
                  <div className="flex items-center gap-4" style={{ flexShrink: 0 }}>
                    {member.linkedin && (
                      <a
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="transition-colors duration-300 hover:text-[var(--brand-primary)]"
                        style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}
                      >
                        <Linkedin size={18} />
                      </a>
                    )}
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        color: isExpanded ? 'var(--brand-primary)' : 'var(--text-muted)',
                        transition: 'transform 0.3s ease, color 0.3s ease',
                        transform: isExpanded ? 'rotate(45deg)' : 'rotate(0deg)',
                        flexShrink: 0,
                      }}
                    >
                      +
                    </div>
                  </div>
                </div>

                {/* Expanded bio */}
                <div
                  style={{
                    maxHeight: isExpanded ? '400px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.5s ease, padding 0.5s ease',
                    paddingBottom: isExpanded ? '32px' : '0',
                    paddingLeft: '76px',
                  }}
                >
                  <p className="oa-body-small" style={{ color: 'var(--text-secondary)', maxWidth: '700px' }}>
                    {member.bio}
                  </p>
                  <div className="flex items-center gap-4" style={{ marginTop: '16px' }}>
                    <span
                      className="lg:hidden block"
                      style={{
                        fontSize: '12px',
                        color: 'var(--text-muted)',
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      Lead: {member.lead}
                    </span>
                    {member.linkedin && (
                      <a
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lg:hidden flex items-center gap-2 transition-colors duration-300 hover:text-[var(--brand-primary)]"
                        style={{
                          fontSize: '13px',
                          color: 'var(--brand-primary)',
                          textDecoration: 'none',
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        <Linkedin size={14} />
                        LinkedIn
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Collective Stats */}
        <div
          className={`flex flex-wrap gap-0 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: '700ms' }}
        >
          {team.collectiveStats.map((stat, i) => (
            <div
              key={i}
              style={{
                padding: '24px 32px',
                borderRight: i < team.collectiveStats.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                flex: '1 1 auto',
                minWidth: '160px',
              }}
            >
              <span
                style={{
                  fontSize: '32px',
                  fontWeight: 700,
                  color: 'var(--brand-primary)',
                  fontFamily: "'Inter', sans-serif",
                  display: 'block',
                  lineHeight: 1,
                  marginBottom: '8px',
                }}
              >
                {stat.value}
              </span>
              <span className="oa-body-muted" style={{ fontSize: '13px' }}>
                {stat.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
